import {useRef, useState, useEffect, useCallback} from 'react';
import {Helmet} from 'react-helmet';
import {Prompt} from 'react-router-dom';

import {SaveButton} from 'shells/button/save-button/save-button';
import {CueCard} from 'shells/cue-card/cue-card';
import {Dialog} from 'shells/dialog/dialog';
import {headTitle} from 'shells/document/head-title';
import {Illustration} from 'shells/illustrations/illustration';
import {Stack} from 'shells/layout/stack/stack';
import {PageHeader} from 'shells/page-header/page-header';
import type {Widget} from 'shells/widgets/types';
import {WidgetsCustomizer} from 'shells/widgets/widgets-customizer';

import type {EntityType, HeaderFieldFragment} from 'nutshell-graphql-types';

import {appHistory} from '../../../history';

import {CreateOrEditWidgetModal} from './create-or-edit-widget-modal';
import {HeaderFieldToggleSection} from './header-field-toggle-section';
import {
    handleHideToggle,
    handleReorder,
    handleEditOrCreateWidget,
    handleRemoveWidget,
    handleToggleHeaderField,
} from './helpers';
import type {WidgetsLocation} from './types';

import styles from './layout-customizer.module.css';

const MAX_CUSTOM_WIDGETS = 10;

interface UseCreateState {
    location: WidgetsLocation;
    widget: Widget | null | undefined;
}

export interface UpdateWidgetLayoutInput {
    sidebar: Widget[];
    workspace: Widget[];
    headerFields: HeaderFieldFragment[];
}

export interface CueCardProps {
    title: string;
    description: string;
    illustrationString: string;
    illustrationWidth?: number;
    ctaOnClick?: () => void;
}

interface Props {
    initialSidebarWidgets: Widget[];
    initialWorkspaceWidgets: Widget[];
    initialHeaderFields?: HeaderFieldFragment[];
    updateWidgetLayout: (input: UpdateWidgetLayoutInput) => void;
    isSaving: boolean;
    isLoading: boolean;
    title?: string;
    titleTooltip?: string;
    subtitle?: string;
    cueCardProps?: CueCardProps;
    entityType: EntityType;
}

export const LayoutCustomizer = (props: Props) => {
    const {
        title = 'Customize layout',
        titleTooltip = 'Any customizations made to this layout will be visible to all users in your organization.',
    } = props;
    const [stateIsDirty, setStateIsDirty] = useState<boolean>(false);
    const [sidebarWidgets, setSidebarWidgets] = useState<Widget[]>(props.initialSidebarWidgets);
    const [workspaceWidgets, setWorkspaceWidgets] = useState<Widget[]>(
        props.initialWorkspaceWidgets
    );
    const [headerFields, setHeaderFields] = useState<HeaderFieldFragment[]>(
        props.initialHeaderFields ?? []
    );
    const [discardChangesConfirmation, setDiscardChangesConfirmation] = useState<boolean>(false);
    const [createOrEditState, setCreateOrEditState] = useState<UseCreateState | null>(null);

    const backButtonRef = useRef<HTMLElement | null | undefined>(null);
    const pendingPathnameRef = useRef<string | null | undefined>(null);

    useEffect(() => {
        if (stateIsDirty) {
            return;
        }

        setSidebarWidgets(props.initialSidebarWidgets);
        setWorkspaceWidgets(props.initialWorkspaceWidgets);
        setHeaderFields(props.initialHeaderFields ?? []);
    }, [
        props.initialSidebarWidgets,
        props.initialWorkspaceWidgets,
        props.initialHeaderFields,
        stateIsDirty,
    ]);

    const handleSave = () => {
        props.updateWidgetLayout({
            sidebar: sidebarWidgets,
            workspace: workspaceWidgets,
            headerFields: headerFields,
        });
    };

    // Handle browser navigation (back button, close tab, etc.)
    // https://developer.mozilla.org/en-US/docs/Web/API/BeforeUnloadEvent
    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (stateIsDirty) {
                e.preventDefault();
                e.returnValue = '';
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [stateIsDirty]);

    const handleBack = useCallback(() => {
        appHistory.goBack();
        setStateIsDirty(false);
    }, []);

    const handleBackButtonClick = useCallback(() => {
        if (stateIsDirty) {
            setDiscardChangesConfirmation(true);
        } else {
            handleBack();
        }
    }, [stateIsDirty, handleBack]);

    // Clear pending navigation when state is no longer dirty
    useEffect(() => {
        if (!stateIsDirty) {
            pendingPathnameRef.current = null;
        }
    }, [stateIsDirty]);

    return (
        <>
            <Prompt
                message={(location) => {
                    if (
                        pendingPathnameRef.current &&
                        pendingPathnameRef.current === location.pathname
                    ) {
                        return true;
                    }

                    // Store the navigating pathname to avoid duplicate prompts
                    pendingPathnameRef.current = location.pathname;

                    return 'You have unsaved changes that will be lost if you leave this page. Are you sure you want to leave?';
                }}
                when={stateIsDirty}
            />
            <PageHeader
                title={title}
                titleTooltip={titleTooltip}
                subtitle={props.subtitle}
                backButton={{
                    onClick: handleBackButtonClick,
                    text: stateIsDirty ? 'Discard changes' : 'Back',
                    getRef: (ref) => {
                        backButtonRef.current = ref;
                    },
                }}
                actionButtons={
                    <SaveButton
                        buttonText='Save changes'
                        isSaving={props.isSaving}
                        // @ts-expect-error
                        onClick={() => {
                            handleSave();
                        }}
                        saveCompleteTimeout={1000}
                        onClearSaveComplete={() => {
                            setStateIsDirty(false);
                            appHistory.goBack();
                        }}
                        disabled={!stateIsDirty}
                        disabledTooltip='No changes to save'
                    />
                }
            />
            <div className={styles.container}>
                <Helmet>
                    <title>{headTitle(title)}</title>
                </Helmet>
                <Stack isFullHeight={true} isFullWidth={true}>
                    <div className={styles['content-container']}>
                        <Stack isHorizontal={true} isFullHeight={true} spacing={12}>
                            <div className={styles['workspace-container']}>
                                {props.cueCardProps && (
                                    <div className={styles['workspace-cue-card-container']}>
                                        <CueCard
                                            title={props.cueCardProps.title}
                                            description={props.cueCardProps.description}
                                            thumbnailIllustration={
                                                <Illustration
                                                    src={props.cueCardProps.illustrationString}
                                                    width={props.cueCardProps.illustrationWidth}
                                                />
                                            }
                                            thumbnailBackgroundColor='blueXlt'
                                            thumbnailPosition='no-resize'
                                            withBookmarkIcon={true}
                                            history={appHistory}
                                            cta={
                                                props.cueCardProps.ctaOnClick
                                                    ? {
                                                          text: 'Learn more',
                                                          actionType: 'modal',
                                                          onClick: props.cueCardProps.ctaOnClick,
                                                      }
                                                    : undefined
                                            }
                                        />
                                    </div>
                                )}
                                <Stack spacing={32} isFullWidth={true}>
                                    {props.initialHeaderFields !== undefined && (
                                        <HeaderFieldToggleSection
                                            headerFields={headerFields}
                                            onToggle={(headerFieldName, isEnabled) => {
                                                handleToggleHeaderField(
                                                    headerFieldName,
                                                    isEnabled,
                                                    headerFields,
                                                    setHeaderFields
                                                );
                                                setStateIsDirty(true);
                                            }}
                                            isLoading={props.isLoading}
                                        />
                                    )}
                                    <WidgetsCustomizer
                                        sectionTitle='Workspace'
                                        maxCustomWidgets={MAX_CUSTOM_WIDGETS}
                                        widgets={workspaceWidgets}
                                        lockedWidgets={[
                                            {
                                                fakeId: 'timeline',
                                                name: 'Timeline',
                                                type: 'TIMELINE',
                                            },
                                        ]}
                                        isLoading={props.isLoading}
                                        onAddWidget={() => {
                                            setCreateOrEditState({
                                                location: 'workspace',
                                                widget: null,
                                            });
                                        }}
                                        handleReorder={(startIndex, endIndex) => {
                                            handleReorder(
                                                startIndex,
                                                endIndex,
                                                workspaceWidgets,
                                                setWorkspaceWidgets
                                            );
                                            setStateIsDirty(true);
                                        }}
                                        handleHideToggle={(widgetId, isHidden) => {
                                            handleHideToggle(
                                                widgetId,
                                                isHidden,
                                                workspaceWidgets,
                                                setWorkspaceWidgets
                                            );
                                            setStateIsDirty(true);
                                        }}
                                        handleEdit={(widgetId) => {
                                            setCreateOrEditState({
                                                location: 'workspace',
                                                widget: workspaceWidgets.find(
                                                    (w) => w.id === widgetId
                                                ),
                                            });
                                        }}
                                        handleRemove={(widgetId) => {
                                            handleRemoveWidget(
                                                widgetId,
                                                workspaceWidgets,
                                                setWorkspaceWidgets
                                            );
                                            setStateIsDirty(true);
                                        }}
                                    />
                                </Stack>
                            </div>
                            <div className={styles['sidebar-container']}>
                                <WidgetsCustomizer
                                    sectionTitle='Sidebar'
                                    isLoading={props.isLoading}
                                    maxCustomWidgets={MAX_CUSTOM_WIDGETS}
                                    widgets={sidebarWidgets}
                                    onAddWidget={() => {
                                        setCreateOrEditState({
                                            location: 'sidebar',
                                            widget: null,
                                        });
                                    }}
                                    handleReorder={(startIndex, endIndex) => {
                                        handleReorder(
                                            startIndex,
                                            endIndex,
                                            sidebarWidgets,
                                            setSidebarWidgets
                                        );
                                        setStateIsDirty(true);
                                    }}
                                    handleHideToggle={(widgetId, isHidden) => {
                                        handleHideToggle(
                                            widgetId,
                                            isHidden,
                                            sidebarWidgets,
                                            setSidebarWidgets
                                        );
                                        setStateIsDirty(true);
                                    }}
                                    handleEdit={(widgetId) => {
                                        setCreateOrEditState({
                                            location: 'sidebar',
                                            widget: sidebarWidgets.find((w) => w.id === widgetId),
                                        });
                                    }}
                                    handleRemove={(widgetId) => {
                                        handleRemoveWidget(
                                            widgetId,
                                            sidebarWidgets,
                                            setSidebarWidgets
                                        );
                                        setStateIsDirty(true);
                                    }}
                                />
                            </div>
                        </Stack>
                    </div>
                </Stack>
            </div>
            {createOrEditState && (
                <CreateOrEditWidgetModal
                    widget={createOrEditState.widget}
                    entityType={props.entityType}
                    onClose={() => setCreateOrEditState(null)}
                    onSave={(newWidget: Widget) => {
                        const widgets =
                            createOrEditState.location === 'workspace'
                                ? workspaceWidgets
                                : sidebarWidgets;
                        const setWidgets =
                            createOrEditState.location === 'workspace'
                                ? setWorkspaceWidgets
                                : setSidebarWidgets;
                        handleEditOrCreateWidget(
                            newWidget,
                            createOrEditState.location,
                            widgets,
                            setWidgets
                        );
                        setCreateOrEditState(null);
                        setStateIsDirty(true);
                    }}
                />
            )}
            {discardChangesConfirmation && (
                <Dialog
                    anchor={backButtonRef.current}
                    question='Are you sure you want to discard changes?'
                    primaryText='Discard'
                    secondaryText='Cancel'
                    primaryVariant='danger'
                    secondaryVariant='secondary'
                    onPrimary={() => {
                        handleBack();
                        setDiscardChangesConfirmation(false);
                    }}
                    onSecondary={() => {
                        setDiscardChangesConfirmation(false);
                    }}
                    customStyles={{
                        width: 220,
                    }}
                />
            )}
        </>
    );
};
